import { Injectable } from '@angular/core';
import {
  StateService,
  TargetState,
  Transition,
  TransitionService,
} from '@uirouter/core';

import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { CompanyStateService } from './company-state.service';

@Injectable({
  providedIn: 'root',
})
export class SelectedCompanyUrlHandlerService {
  private initialized = false;

  constructor(
    private stateService: StateService,
    private companyStateService: CompanyStateService,
    private broadcasterService: BroadcasterService,
    private transitionService: TransitionService,
  ) {}

  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.broadcasterService.on(
      'risevision.company.selectedCompanyChanged',
      () => {
        this.updateUrl();
      },
    );

    // Inject the selected company's cid as part of the entering transition
    // (a redirect), never as a post-success `go('.')`. A post-success `go('.')`
    // starts a competing transition that supersedes the entering one before
    // UI-Router commits the routed component's injector, which nulls the
    // `Transition` injected into components such as ModeratorJoinComponent.
    // Deferring the `go('.')` (micro/macrotask) avoids that, but then the cid
    // lands a beat later and breaks flows that read the URL right after
    // navigation. Redirecting keeps the cid in sync with the navigation itself.
    this.transitionService.onBefore({}, (transition) =>
      this.appendSelectedCompanyCid(transition),
    );

    // Adopt a company chosen through the URL's cid. This never starts a
    // competing transition (only `switchCompany`), so it is safe post-success.
    this.transitionService.onSuccess({}, () => {
      this.updateSelectedCompanyFromUrl();
    });
  }

  updateUrl(): void {
    const selectedCompanyId = this.companyStateService.getSelectedCompanyId();

    // This parameter is only appended to the url if the user is logged in.
    // Do not apply during a $state transition (the onBefore hook handles those).
    if (
      selectedCompanyId &&
      this.stateService.params['cid'] !== selectedCompanyId &&
      !this.stateService.transition &&
      this.stateService.current?.name
    ) {
      // Mutate current params (legacy parity) so other selectedCompanyChanged
      // listeners see the updated cid before navigation completes.
      const params = this.stateService.params;
      params['cid'] = selectedCompanyId;

      // Transition may be superseded; leave params for the next sync.
      this.stateService.go('.', params).catch(() => undefined);
    }
  }

  /**
   * Redirects the entering transition so the selected company's cid is present
   * in the URL when the state resolves, without starting a competing transition.
   * Returns a redirect TargetState when the cid must be added, otherwise nothing.
   */
  appendSelectedCompanyCid(transition: Transition): TargetState | void {
    const toParams = transition.params('to');
    const selectedCompanyId = this.companyStateService.getSelectedCompanyId();

    // Leave the URL untouched when it already carries a cid (even a different
    // one — updateSelectedCompanyFromUrl adopts it after the transition) or when
    // there is no active company to sync.
    if (toParams['cid'] || !selectedCompanyId) {
      return undefined;
    }

    // `replace` so the cid-less URL does not leave its own history entry.
    return transition
      .targetState()
      .withParams({ ...toParams, cid: selectedCompanyId })
      .withOptions({ location: 'replace' });
  }

  updateSelectedCompanyFromUrl(): void {
    const newCompanyId = this.stateService.params['cid'];

    if (
      newCompanyId &&
      this.companyStateService.getUserCompanyId() &&
      newCompanyId !== this.companyStateService.getSelectedCompanyId()
    ) {
      // The CID in the URL differs from the active company; switch to it.
      this.companyStateService.switchCompany(newCompanyId);
    }
  }
}
